import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/api';
import { setPasswordRecoveryMode, useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/Toast';
import { Button, Input, Card } from '../../components/ui/Kit';

const OTP_COOLDOWN_MS = 60_000;
const OTP_SENT_NOTICE = 'If that email has an account, a code is on its way';

function loginErrorFromSearch(searchParams) {
  if (searchParams.get('expired') === '1') {
    return 'Your sign-in link expired, please log in again';
  }
  if (searchParams.get('error') === 'profile') {
    return 'Your account could not be loaded. Please log in again.';
  }
  if (searchParams.get('error') === 'otp_expired') {
    return 'Your temporary sign-in has expired — please log in again.';
  }
  if (searchParams.get('error') === 'deactivated') {
    return 'Your account has been deactivated. Contact your admin.';
  }
  return '';
}

async function stampAuthMethod(data) {
  if (!supabase) return;
  const { error } = await supabase.auth.updateUser({ data });
  if (error) throw error;
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [mode, setMode] = useState('password');
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(() => loginErrorFromSearch(searchParams));
  const [passwordFailed, setPasswordFailed] = useState(false);
  const [otpNotice, setOtpNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpCooldownUntil, setOtpCooldownUntil] = useState(0);
  const [otpCooldownLeft, setOtpCooldownLeft] = useState(0);
  const { login, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const { addToast } = useToast();

  useEffect(() => {
    const fromQuery = loginErrorFromSearch(searchParams);
    if (fromQuery) setError(fromQuery);
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get('password-reset') !== 'success') return;
    navigate('/login', { replace: true });
    addToast({
      type: 'success',
      title: 'Password updated — please sign in',
      message: 'Sign in with your new password to continue.',
    });
  }, [searchParams, addToast, navigate]);

  useEffect(() => {
    if (!otpCooldownUntil) {
      setOtpCooldownLeft(0);
      return undefined;
    }
    const tick = () => {
      const left = Math.max(0, Math.ceil((otpCooldownUntil - Date.now()) / 1000));
      setOtpCooldownLeft(left);
      if (left <= 0) setOtpCooldownUntil(0);
    };
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [otpCooldownUntil]);

  const startOtpFlow = (e) => {
    e?.preventDefault();
    setError('');
    setOtpNotice('');
    setCode('');
    setMode('otp-email');
  };

  const backToPassword = (e) => {
    e?.preventDefault();
    setError('');
    setOtpNotice('');
    setCode('');
    setMode('password');
  };

  const sendOtp = useCallback(async () => {
    const trimmed = email.trim();
    if (!trimmed || otpCooldownLeft > 0) return;
    setError('');
    setLoading(true);
    try {
      if (supabase) {
        await supabase.auth.signInWithOtp({
          email: trimmed,
          options: { shouldCreateUser: false },
        });
      }
    } catch {
      // Never confirm or deny that the account exists.
    } finally {
      setLoading(false);
      setOtpCooldownUntil(Date.now() + OTP_COOLDOWN_MS);
      setOtpNotice(OTP_SENT_NOTICE);
      setMode('otp-code');
    }
  }, [email, otpCooldownLeft]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setPasswordFailed(false);
    setLoading(true);
    try {
      await login(email, password);
      try {
        await stampAuthMethod({ last_auth_method: 'password', otp_login_at: null });
      } catch (metaErr) {
        console.error('Could not clear OTP session flag after password login:', metaErr);
      }
      const redirect = searchParams.get('redirect');
      if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
        navigate(redirect);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setPasswordFailed(true);
      setError(err.message || 'Failed to login');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    await sendOtp();
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    const token = code.replace(/\D/g, '');
    if (token.length !== 6) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    if (!supabase) {
      setError('Email code sign-in is not available.');
      return;
    }
    setLoading(true);
    try {
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        email: email.trim(),
        token,
        type: 'email',
      });
      if (verifyError || !data?.session) {
        setError(verifyError?.message || 'That code is invalid or has expired. Try again or resend.');
        return;
      }
      setPasswordRecoveryMode(false);
      try {
        await stampAuthMethod({
          last_auth_method: 'otp',
          otp_login_at: new Date().toISOString(),
        });
      } catch (metaErr) {
        console.error('Could not stamp OTP session metadata:', metaErr);
      }
      const profile = await refreshUser();
      if (!profile?.id || !profile.agency_id || !profile.role || profile.is_active !== true) {
        try {
          await logout();
        } catch {
          // ignore
        }
        setError('Your account could not be loaded. Please log in again.');
        return;
      }
      const redirect = searchParams.get('redirect');
      if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
        navigate(redirect);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.message || 'That code is invalid or has expired. Try again or resend.');
    } finally {
      setLoading(false);
    }
  };

  const otpCooling = otpCooldownLeft > 0;
  const sendLabel = otpCooling ? `Send code (${otpCooldownLeft}s)` : 'Send code';
  const resendLabel = otpCooling ? `Resend code (${otpCooldownLeft}s)` : 'Resend code';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="max-w-md w-full p-8 shadow-xl border-0 ring-1 ring-gray-200">
        <div className="text-center mb-8">
          <div className="mx-auto h-12 w-12 bg-primary rounded-lg shadow-sm flex items-center justify-center mb-4">
             <span className="text-white font-bold text-2xl">P</span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">
            {mode === 'password' ? 'Sign in to your agency' : 'Sign in with a one-time code'}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {mode === 'password' ? (
              <>
                Or <Link to="/signup" className="font-medium text-primary hover:text-primary-dark">Request access</Link>
              </>
            ) : (
              <button
                type="button"
                onClick={backToPassword}
                className="font-medium text-primary hover:text-primary-dark"
              >
                Use password instead
              </button>
            )}
          </p>
        </div>

        {mode === 'password' && (
          <form onSubmit={handlePasswordSubmit} className="space-y-6">
            {error && (
              <div className="p-3 bg-red-50 text-red-500 text-sm rounded-md border border-red-100">
                <p>{error}</p>
                {passwordFailed && (
                  <button
                    type="button"
                    onClick={startOtpFlow}
                    className="mt-2 font-medium text-red-700 hover:text-red-800 underline underline-offset-2"
                  >
                    Trouble signing in? Email me a one-time code instead
                  </button>
                )}
              </div>
            )}

            <Input
              label="Email address"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="agent@example.com"
              autoComplete="email"
            />

            <div>
              <Input
                label="Password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
              />
              <div className="mt-2 text-right">
                <Link to="/forgot-password" className="text-sm font-medium text-primary hover:text-primary-dark">
                  Forgot password?
                </Link>
              </div>
              <div className="mt-2">
                <button
                  type="button"
                  onClick={startOtpFlow}
                  className="text-sm font-medium text-primary hover:text-primary-dark"
                >
                  Login with a one-time code instead
                </button>
              </div>
            </div>

            <Button type="submit" className="w-full" isLoading={loading} size="lg">
              Sign in
            </Button>
          </form>
        )}

        {mode === 'otp-email' && (
          <form onSubmit={handleSendCode} className="space-y-6">
            {error && (
              <div className="p-3 bg-red-50 text-red-500 text-sm rounded-md border border-red-100">{error}</div>
            )}
            {otpNotice && (
              <div className="p-3 bg-green-50 text-green-800 text-sm rounded-md border border-green-100">{otpNotice}</div>
            )}

            <Input
              label="Email address"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="agent@example.com"
              autoComplete="email"
            />

            <Button type="submit" className="w-full" isLoading={loading} disabled={otpCooling} size="lg">
              {sendLabel}
            </Button>
          </form>
        )}

        {mode === 'otp-code' && (
          <form onSubmit={handleVerifyCode} className="space-y-6">
            {error && (
              <div className="p-3 bg-red-50 text-red-500 text-sm rounded-md border border-red-100">{error}</div>
            )}
            {otpNotice && (
              <div className="p-3 bg-green-50 text-green-800 text-sm rounded-md border border-green-100">{otpNotice}</div>
            )}

            <Input
              label="6-digit code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
            />

            <Button type="submit" className="w-full" isLoading={loading} size="lg">
              Verify & sign in
            </Button>

            <div className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={() => void sendOtp()}
                disabled={otpCooling || loading}
                className="text-sm font-medium text-primary hover:text-primary-dark disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {resendLabel}
              </button>
              <button
                type="button"
                onClick={backToPassword}
                className="text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                Use password instead
              </button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
